import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { PlusIcon } from '@phosphor-icons/react'

import { Button, Pagination, Text, ToastContext } from '@reveni-io/reveni-ui'

import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useClientPagination from '@/common/hooks/useClientPagination/useClientPagination'
import useLocations from '@/common/hooks/useLocations/useLocations'
import usePackaging from '@/common/hooks/usePackaging/usePackaging'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import routes from '@/Router/routes.constants'

import DeleteLocationModal from './DeleteLocationModal/DeleteLocationModal'
import useCreateDefaultDeliveryMethods from './hooks/useCreateDefaultDeliveryMethods/useCreateDefaultDeliveryMethods'
import useDeleteLocation from './hooks/useDeleteLocation/useDeleteLocation'
import useLogisticsPreferences from './hooks/useLogisticsPreferences/useLogisticsPreferences'
import Location from './Location/Location'
import LocationsConfigurationSkeleton from './LocationsConfigurationSkeleton/LocationsConfigurationSkeleton'
import ReturnMethodsSuggestionBanner from './ReturnMethodsSuggestionBanner/ReturnMethodsSuggestionBanner'
import { getReturnMethodsSuggestionLocation } from './helpers'
import * as Styles from './styles'

const LocationsConfiguration = () => {
  const { t } = useTranslation()
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()
  const { locations, fetchingLocations, refetchLocations } = useLocations({ storeId: info?.selectedStore?.id })
  const { deliveryMethods, fetchingLogisticsPreferences } = useLogisticsPreferences({
    storeId: info?.selectedStore?.id,
  })
  const { packaging } = usePackaging({ storeId: info?.selectedStore?.id })
  const { regions } = useRegions()
  const { elementsPaginated, hasNextPage, hasPreviousPage, numberOfPages, onChangePage, page } = useClientPagination({
    elements: locations,
    pageSize: 10,
  })
  const reactRouterDomLocation = useLocation()
  const navigate = useNavigate()
  const {
    showDeleteLocationModal,
    closeDeleteLocationModal,
    openDeleteLocationModal,
    associatedDeliveryMethods,
    associatedShippingMethods,
    changeLocationIdOfDeliveryMethod,
    changeLocationIdOfShippingMethod,
    isFormValid,
    deleteLocation,
    deletingLocation,
    locationToDelete,
    isDeleteLocationActionDisabled,
  } = useDeleteLocation({
    storeId: info?.selectedStore?.id,
    refetch: refetchLocations,
    allowsShipments: info?.selectedStore?.preferences?.allows_shipments,
    deliveryMethods,
  })
  const { createDefaultDeliveryMethods, creatingDefaultDeliveryMethods } = useCreateDefaultDeliveryMethods({
    storeId: info?.selectedStore?.id,
    updateContext,
    setToastInfo,
  })
  const suggestionLocation = getReturnMethodsSuggestionLocation({
    locations,
    deliveryMethods,
    canEditLogistics: hasPermission(userPermissions.editLogistics),
  })

  const onCreateDefaultDeliveryMethods = () => createDefaultDeliveryMethods(suggestionLocation.id)

  const onNewLocation = () => {
    navigate(routes.configurationPage.logistics.locationDetailConfiguration.replace(':id', 'new'), {
      state: { previousLocation: reactRouterDomLocation },
    })
  }

  const onEdit = location => {
    navigate(routes.configurationPage.logistics.locationDetailConfiguration.replace(':id', location.id), {
      state: {
        childPageTitle: location?.internal_name,
        selectedLocation: location,
        previousLocation: reactRouterDomLocation,
      },
    })
  }

  const onDuplicate = location => {
    navigate(routes.configurationPage.logistics.locationDetailConfiguration.replace(':id', 'duplicate'), {
      state: {
        childPageTitle: location?.internal_name,
        selectedLocation: location,
        previousLocation: reactRouterDomLocation,
      },
    })
  }

  if (fetchingLocations || fetchingLogisticsPreferences) {
    return <LocationsConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {suggestionLocation && (
        <ReturnMethodsSuggestionBanner
          onCreate={onCreateDefaultDeliveryMethods}
          creating={creatingDefaultDeliveryMethods}
        />
      )}
      <Text type="bodyMRegular">{t('Define las ubicaciones que utilizarán tus métodos de devolución')}:</Text>
      <Styles.LocationsListContainer>
        {elementsPaginated?.map(location => (
          <Location
            info={location}
            key={location.id}
            packaging={packaging}
            onNewLocation={onNewLocation}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={openDeleteLocationModal}
            isDeleteDisabled={isDeleteLocationActionDisabled(elementsPaginated, location)}
          />
        ))}
      </Styles.LocationsListContainer>
      <Styles.ActionsContainer>
        <Button variant="text" onClick={onNewLocation}>
          <PlusIcon />
          {t('Añadir nueva ubicación')}
        </Button>
        <Pagination
          count={numberOfPages}
          next={hasNextPage}
          previous={hasPreviousPage}
          onChangePage={onChangePage}
          page={page}
        />
      </Styles.ActionsContainer>
      <DeleteLocationModal
        show={showDeleteLocationModal}
        onClose={closeDeleteLocationModal}
        associatedDeliveryMethods={associatedDeliveryMethods}
        associatedShippingMethods={associatedShippingMethods}
        locations={elementsPaginated}
        regions={regions}
        changeLocationIdOfDeliveryMethod={changeLocationIdOfDeliveryMethod}
        changeLocationIdOfShippingMethod={changeLocationIdOfShippingMethod}
        isFormValid={isFormValid}
        deleting={deletingLocation}
        onDelete={deleteLocation}
        locationToDelete={locationToDelete}
      />
    </Styles.Container>
  )
}

export default LocationsConfiguration
